// ==============================================
// Runtime Drift Card
// ==============================================
//
// A dedicated Health-tab module that surfaces the launcher's LIVE runtime
// SystemUI state (window-gesture insets + display geometry) and contrasts it
// with the STORED hardening. It exists because the recurring back-gesture
// desync lives in SystemUI at runtime while every stored-value section reads
// perfectly healthy — so this is the one place the desync becomes visible.
//
// Purely presentational. The verdict is computed by computeRuntimeDrift() in
// utils/kioskHealth.ts; this renders it as a stored-vs-runtime comparison with
// a prominent banner when a desync is suspected.

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import HealthModuleCard, { type HealthModuleTone } from './HealthModuleCard';
import {
  computeRuntimeDrift,
  type KioskStateAny,
  type RuntimeDriftField,
  type RuntimeDriftResult,
} from '../../utils/kioskHealth';

const STATUS_TONE: Record<RuntimeDriftResult['status'], HealthModuleTone> = {
  ok: 'ok',
  info: 'neutral',
  warn: 'warn',
  fail: 'fail',
  incomplete: 'neutral',
};

const VALUE_TONE: Record<RuntimeDriftField['tone'], string> = {
  ok: 'text-green-400',
  warn: 'text-amber-400',
  fail: 'text-red-400',
  unreadable: 'text-slate-500 italic',
  info: 'text-slate-300',
};

function statusText(result: RuntimeDriftResult): string {
  switch (result.status) {
    case 'fail':
      return 'Gesture desync suspected';
    case 'warn':
      return 'Drift detected';
    case 'incomplete':
      return result.insetsCaptured ? 'Partial snapshot' : 'Insets not captured';
    default:
      return 'In sync';
  }
}

function Banner({ result }: { result: RuntimeDriftResult }) {
  if (result.status === 'fail') {
    return (
      <div className="rounded-lg border border-red-700/50 bg-red-900/20 p-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none text-red-400 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-red-200">{result.summary}</p>
            <p className="text-xs text-red-100/80">
              Stored hardening is correct but the framework is acting on stale SystemUI state.
              Rebooting the device clears it. The launcher cannot self-heal this from a settings
              rewrite, so a reboot (or the eventual SystemUI re-sync fix) is the remedy.
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (result.status === 'warn') {
    return (
      <div className="rounded-lg border border-amber-700/50 bg-amber-900/20 p-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none text-amber-400 mt-0.5" />
          <p className="text-sm text-amber-100/90">{result.summary}</p>
        </div>
      </div>
    );
  }
  if (result.status === 'incomplete') {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-4">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 flex-none text-slate-400 mt-0.5" />
          <p className="text-sm text-slate-300">{result.summary}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-green-700/40 bg-green-900/15 p-4">
      <div className="flex items-start gap-3">
        <CheckCircleIcon className="h-5 w-5 flex-none text-green-400 mt-0.5" />
        <p className="text-sm text-green-100/90">{result.summary}</p>
      </div>
    </div>
  );
}

function ComparisonRow({ field }: { field: RuntimeDriftField }) {
  const drifted = field.tone === 'fail' || field.tone === 'warn';
  return (
    <div
      className={`grid grid-cols-[1fr,auto,auto] items-baseline gap-3 px-3 py-1.5 text-sm ${
        drifted ? 'bg-amber-900/10' : ''
      }`}
    >
      <div className="min-w-0">
        <span className="text-slate-300 truncate" title={field.label}>
          {field.label}
        </span>
        {field.note && (
          <p className={`text-[10px] ${field.tone === 'fail' ? 'text-red-400/90' : 'text-amber-400/80'}`}>
            {field.note}
          </p>
        )}
      </div>
      <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
        stored {field.stored}
      </span>
      <span className={`font-mono text-xs whitespace-nowrap ${VALUE_TONE[field.tone]}`}>
        {field.runtime}
      </span>
    </div>
  );
}

export interface RuntimeDriftCardProps {
  state: KioskStateAny | null | undefined;
}

/**
 * Renders the Runtime Drift health module. Returns null when the snapshot has
 * no runtime_drift block (older launcher builds) so the card only appears once
 * a device reports it — avoiding fleet-wide noise during rollout.
 */
export default function RuntimeDriftCard({ state }: RuntimeDriftCardProps) {
  const result = computeRuntimeDrift(state);
  if (!result.present) return null;

  return (
    <HealthModuleCard
      title="Runtime drift"
      status={STATUS_TONE[result.status]}
      statusText={statusText(result)}
      subtitle={
        <span className="text-xs text-slate-500">
          Live SystemUI gesture &amp; geometry vs stored hardening — the signal that exposes the
          back-gesture desync.
        </span>
      }
      defaultExpanded={result.status === 'fail' || result.status === 'warn'}
    >
      <Banner result={result} />
      <div className="rounded-lg border border-slate-700 bg-slate-900/40">
        <div className="grid grid-cols-[1fr,auto,auto] gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-700/50">
          <span>Signal</span>
          <span className="text-right">Stored</span>
          <span className="text-right">Live runtime</span>
        </div>
        <div className="py-1">
          {result.fields.map((f, i) => (
            <ComparisonRow key={`${f.label}-${i}`} field={f} />
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 italic">
        Captured during the last update check, not live. Insets are read from the launcher window;
        a reboot clears a runtime desync.
      </p>
    </HealthModuleCard>
  );
}
